import { destinations } from '../config/destinations'
import { useParallax } from '../hooks/useParallax'
import { useReveal } from '../hooks/useReveal'
import { Icon } from './ui/Icon'

/** Per-column drift, so the grid gains depth as it passes. */
const DRIFT = [24, 10, 20, 8]

/** Cards deal in one at a time, left to right, restarting on each row. */
const CARD_STAGGER = 140
const CARD_LEAD_IN = 220

export function CommunityTrips() {
  const ref = useReveal()
  useParallax(ref)

  return (
    <section id="community" ref={ref} className="scroll-mt-20 bg-[var(--surface)] pt-12 pb-24 sm:pt-16 sm:pb-32">
      <div className="mx-auto max-w-[88rem] px-5 sm:px-8">
        <header className="mx-auto max-w-3xl text-center" data-parallax="40">
          <p className="text-base font-bold text-brand-600 dark:text-brand-300" data-reveal>
            Community Plans
          </p>
          <h2
            className="mt-4 text-[clamp(2rem,4.4vw,3.4rem)] leading-[1.08] font-bold"
            data-reveal="rise"
            style={{ '--reveal-delay': '90ms' }}
          >
            Real Trips, Planned By Real Travellers
          </h2>
          <p
            className="mt-5 text-[1.05rem] leading-relaxed"
            data-reveal="rise"
            style={{ '--reveal-delay': '170ms' }}
          >
            Every itinerary here started as someone else's idea. Open one and make it yours.
          </p>
          <span
            className="mx-auto mt-8 block h-px w-24 bg-brand-500/60"
            data-reveal="draw"
            style={{ '--reveal-delay': '300ms' }}
            aria-hidden="true"
          />
        </header>

        <ul className="mt-14 grid auto-rows-[14.5rem] grid-cols-2 gap-4 sm:auto-rows-[16rem] lg:mt-18 lg:grid-cols-4 lg:gap-5">
          {destinations.map((destination, i) => (
            <li
              key={destination.id}
              data-reveal="rise"
              data-parallax={DRIFT[i % 4]}
              style={
                { '--reveal-delay': `${CARD_LEAD_IN + (i % 4) * CARD_STAGGER}ms` }
              }
            >
              <a
                href="#community"
                className="group relative block h-full w-full overflow-hidden rounded-2xl transition-[transform,box-shadow] duration-500 ease-[var(--ease-out-soft)] hover:-translate-y-1.5 hover:shadow-[0_30px_60px_-30px_rgb(45_101_113/0.55)] focus-visible:-translate-y-1.5"
              >
                <img
                  src={destination.image}
                  alt={`${destination.title} — ${destination.summary}`}
                  loading="lazy"
                  decoding="async"
                  className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 ease-[var(--ease-out-soft)] group-hover:scale-107 group-focus-visible:scale-107"
                />
                <span className="absolute inset-0 bg-linear-to-t from-ink-950/85 via-ink-950/15 to-transparent transition-opacity duration-500 group-hover:from-ink-950/92 group-hover:via-ink-950/35 group-focus-visible:from-ink-950/92" />
                <span className="absolute inset-x-0 bottom-0 p-5 sm:p-6">
                  <span className="block text-[1.05rem] leading-tight font-bold text-white sm:text-xl">
                    {destination.title}
                  </span>
                  <span className="mt-1.5 line-clamp-2 block max-w-[34ch] text-[0.85rem] leading-snug text-white/0 transition-colors duration-500 group-hover:text-white/80 group-focus-visible:text-white/80 max-lg:text-white/70">
                    {destination.summary}
                  </span>
                </span>
              </a>
            </li>
          ))}
        </ul>

        <div
          className="mt-14 flex justify-center"
          data-reveal="rise"
          style={{ '--reveal-delay': '160ms' }}
        >
          <a
            href="#community"
            className="group inline-flex items-center gap-2.5 rounded-full bg-brand-600 px-7 py-4 text-base font-semibold text-white transition-[background-color,transform] duration-200 hover:-translate-y-0.5 hover:bg-brand-700"
          >
            Browse Community Trips
            <Icon
              name="arrow"
              className="h-[1.15rem] w-[1.15rem] transition-transform duration-200 group-hover:translate-x-1"
            />
          </a>
        </div>
      </div>
    </section>
  )
}
